import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataProviderService } from 'src/app/services/data-provider.service';
import { MessagingService } from 'src/app/services/messaging.service';
import { RestService } from 'src/app/services/rest.service';
import { VisitDayElementComponent } from './components/visit-day-element/visit-day-element.component';
import { DictionaryService } from 'src/app/services/dictionary.service';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { SelectOption, SnackbarService } from '@bcl/design-system';
import { MatDialog } from '@angular/material/dialog';
import { TimesheetDialogComponent } from './components/timesheet-dialog/timesheet-dialog.component';
import { TimesheetApiService } from 'src/app/services/timesheet.service';
import { LoadingController, Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { AccordionService } from 'src/app/services/accordion.service';
import { BackButtonService } from 'src/app/services/back-button.service';
import { ViewWillEnter } from '@ionic/angular';
import { TaskFilterService } from 'src/app/services/task-filter.service';
import { AreaApiService } from 'src/app/pages/area/area.service';
import { LocationDetailsOptions } from 'src/app/pages/area/area.model';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-visit-day',
  templateUrl: './visit-day.page.html',
  styleUrls: ['./visit-day.page.scss'],
})
export class VisitDayPage
  implements OnInit, AfterViewInit, OnDestroy, ViewWillEnter
{
  @ViewChildren(VisitDayElementComponent)
  childComponents: QueryList<VisitDayElementComponent>;

  public dayNumber: number;
  public date: string;
  public jobs: any = [];
  public googleMapsUrl = 'https://www.google.com/maps/dir/';
  public dateStarted = false;
  public dateEnded = false;
  public isPaused = false;
  public days: any[] = [
    {
      key: 'DAYS.FOURTEEN_DAYS_AGO',
      value: -14,
    },
    {
      key: 'DAYS.THIRTEEN_DAYS_AGO',
      value: -13,
    },
    {
      key: 'DAYS.TWELVE_DAYS_AGO',
      value: -12,
    },
    {
      key: 'DAYS.ELEVEN_DAYS_AGO',
      value: -11,
    },
    {
      key: 'DAYS.TEN_DAYS_AGO',
      value: -10,
    },
    {
      key: 'DAYS.NINE_DAYS_AGO',
      value: -9,
    },
    {
      key: 'DAYS.EIGHT_DAYS_AGO',
      value: -8,
    },
    {
      key: 'DAYS.SEVEN_DAYS_AGO',
      value: -7,
    },
    {
      key: 'DAYS.SIX_DAYS_AGO',
      value: -6,
    },
    {
      key: 'DAYS.FIVE_DAYS_AGO',
      value: -5,
    },
    {
      key: 'DAYS.FOUR_DAYS_AGO',
      value: -4,
    },
    {
      key: 'DAYS.THREE_DAYS_AGO',
      value: -3,
    },
    {
      key: 'DAYS.TWO_DAYS_AGO',
      value: -2,
    },
    {
      key: 'DAYS.YESTERDAY',
      value: -1,
    },
    {
      key: 'DAYS.TODAY',
      value: 0,
    },
    {
      key: 'DAYS.TOMORROW',
      value: 1,
    },
    {
      key: 'DAYS.TWO_DAYS',
      value: 2,
    },
    {
      key: 'DAYS.THREE_DAYS',
      value: 3,
    },
    {
      key: 'DAYS.FOUR_DAYS',
      value: 4,
    },
    {
      key: 'DAYS.FIVE_DAYS',
      value: 5,
    },
  ];
  public timesheetLabels: SelectOption[] = [];
  public scoreStatusLabels: SelectOption[] = [];
  public openVisitId: string | null = null;
  public showAll = false;
  public warehouseOptionsLoaded = false;
  public isLoadingPresenting = false;
  public loadingDialog: HTMLIonLoadingElement;
  public hasError = false;
  public isLoading = true;
  private backButtonSubscription: Subscription;
  private destroyed$ = new Subject<void>();

  private warehouseOptions: SelectOption[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataProviderService,
    private messaging: MessagingService,
    private restService: RestService,
    private cdr: ChangeDetectorRef,
    private dictionaryService: DictionaryService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private timesheetApiService: TimesheetApiService,
    private backButtonService: BackButtonService,
    private platform: Platform,
    private taskFilterService: TaskFilterService,
    private accordionService: AccordionService,
    private areaApiService: AreaApiService,
    private loadingController: LoadingController,
    private translatePipe: TranslatePipe
  ) {}

  get filteredJobs() {
    if (this.showAll) {
      return this.jobs;
    }

    // When showAll is false: show all non-visits and only incomplete/reopened visits
    return this.jobs.filter((job) => {
      // Always show non-visits
      if (job.nonVisit) {
        return true;
      }
      // For visits: show if not completed OR if reopened
      return !job.isCompleted || job.isReopened;
    });
  }

  ionViewWillEnter(): void {
    this.backButtonService.showBackButton({
      show: true,
      url: '/home',
    });
  }

  ngOnInit(): void {
    this.backButtonSubscription =
      this.platform.backButton.subscribeWithPriority(10, () => {
        this.dialog.closeAll();
        this.goBack();
      });

    // Initialize showAll from the shared service
    this.showAll = this.taskFilterService.getShowAll();

    // Subscribe to showAll changes
    this.taskFilterService.showAll$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showAll) => {
        this.showAll = showAll;
      });

    // Listen to dataSynchronized events to refresh visit data
    this.messaging.dataSynchronized
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        // Force refresh from API to ensure we get the latest data with proper visit status
        this.forceRefreshFromAPI();
      });

    this.route.params.subscribe((params) => {
      this.date = params?.date;
      this.dayNumber = parseInt(params?.dayId, 10);
      this.getData(this.date);
    });

    this.route.queryParams.subscribe((queryParams) => {
      this.openVisitId = queryParams.openVisitId
        ? queryParams.openVisitId
        : null;
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.cdr.detectChanges();
      this.accessChildComponents();
    }, 200);
  }

  accessChildComponents(): void {
    const childComponentsArray: VisitDayElementComponent[] =
      this.childComponents?.toArray();

    this.route.fragment.subscribe((fragment) => {
      const child = childComponentsArray?.find(
        (component) =>
          parseInt(component.idVisit, 10) === parseInt(fragment, 10)
      );

      if (child) {
        child.showItemList = true;
      }
      if (fragment) {
        this.scrollToVisit(fragment);
      }
    });
  }

  public async getData(date: string) {
    this.isLoadingPresenting = true;
    this.isLoading = true;
    this.hasError = false;
    await this.showLoading();

    combineLatest({
      jobs: this.dataService.getEljakimJobs(date),
      timesheetLabels:
        this.dictionaryService.getDictionaryItemsByKey('PC.TIMESHEET.LABEL'),
      scoreStatusLabels:
        this.dictionaryService.getDictionaryItemsByKey('PC.SCORE.STATUS'),
      locationOptions: this.areaApiService.getLocationDetailsOptions(),
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ({
          jobs,
          timesheetLabels,
          scoreStatusLabels,
          locationOptions,
        }) => {
          // Check if jobs data is valid
          if (!jobs || !Array.isArray(jobs)) {
            console.error('Invalid jobs data received:', jobs);
            this.handleDataLoadError();
            return;
          }

          this.jobs = jobs;
          this.timesheetLabels = timesheetLabels.map((label) => ({
            value: label.value,
            label: label.name,
          }));
          this.scoreStatusLabels = scoreStatusLabels.map((label) => ({
            value: label.value,
            label: label.name,
          }));

          this.warehouseOptions =
            locationOptions.monitor.product.warehouses || [];
          this.warehouseOptionsLoaded = true;

          if (jobs.length > 0) {
            this.isPaused = jobs[0]?.paused || false;
            this.dateStarted = jobs[0]?.dayStarted || false;
            this.dateEnded = jobs[0]?.dayEnded || false;
          }

          this.processVisitData();

          this.assignLetters();

          this.accessChildComponents();

          this.isLoading = false;
          this.hasError = false;

          if (this.isLoadingPresenting) {
            this.isLoadingPresenting = false;
            this.loadingDialog.dismiss();
          }

          if (this.openVisitId) {
            setTimeout(() => {
              this.cdr.detectChanges();
              setTimeout(() => {
                this.scrollToVisit(this.openVisitId);
              }, 500);
            }, 100);
          }
        },
        error: (error) => {
          console.error('Error loading visit day data:', error);
          this.handleDataLoadError();
        },
      });
  }

  public processVisitData() {
    if (!this.jobs || !Array.isArray(this.jobs)) {
      return;
    }

    // Process visit data the same way as home page to ensure locationName is available
    this.jobs.forEach((item) => {
      item.nonVisit = item.nonVisit || false;
      item.plannedStartTime = item.plannedStartTime?.slice(0, 5);
      item.plannedEndTime = item.plannedEndTime?.slice(0, 5);
      item.visitTypeTab = 2; // This ensures locationName is used for company name
    });
  }

  onShowAllChange(): void {
    this.taskFilterService.setShowAll(this.showAll);
    // Force change detection to ensure components re-render properly
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  shouldBeInitiallyOpen(visit: any, index: number): boolean {
    if (this.openVisitId !== null) {
      return visit.visitId?.toString() === this.openVisitId?.toString();
    }
    return index === 0;
  }

  trackByVisit(index: number, visit: any): string {
    return `${visit.visitId}-${this.showAll}`;
  }

  public assignLetters() {
    if (!this.jobs || !Array.isArray(this.jobs)) {
      return;
    }

    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    this.jobs
      .filter((el) => !el.nonVisit)
      .forEach((obj, index) => {
        obj.letter = alphabet[index % alphabet.length];
      });
    this.jobs
      .filter((el) => el.nonVisit)
      .forEach((obj, index) => {
        obj.letter = 'NV';
      });
  }

  public forceRefreshFromAPI() {
    this.restService.email$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((email) => {
        if (email) {
          this.dataService
            .getEljakimJobsPlan(email, new Date(this.date), true)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
              next: () => {
                this.getData(this.date);
              },
              error: (error) => {
                console.error('Error refreshing visit data:', error);
                this.getData(this.date);
              },
            });
        }
      });
  }

  generateRouteLink() {
    if (!this.jobs || !Array.isArray(this.jobs) || this.jobs.length === 0) {
      this.snackbarService.open('red', 'VISIT_DAY.MESSAGES.NO_VISITS_TO_ROUTE');
      return;
    }

    let url = this.googleMapsUrl;
    this.jobs.forEach((visit) => {
      if (visit.locationLat && visit.locationLng) {
        url += `/${visit.locationLat},${visit.locationLng}`;
      }
    });

    window.open(encodeURI(url), '_system', 'location=no');
  }

  generateTimesheet(type: string) {
    if (
      (type === 'start_day' || type === 'end_day') &&
      !this.warehouseOptionsLoaded
    ) {
      this.areaApiService.getLocationDetailsOptions().subscribe((options) => {
        this.warehouseOptions = options.monitor.product.warehouses || [];
        this.warehouseOptionsLoaded = true;
        this.openTimesheetDialog(type);
      });
    } else {
      this.openTimesheetDialog(type);
    }
  }

  public showDay(status: number): string {
    const statusToShow = this.days.find((x) => x.value === status)?.key;

    if (typeof statusToShow === 'string') {
      return statusToShow;
    } else {
      return '';
    }
  }

  public goBack(): void {
    this.router.navigate(['/home']);
  }

  public goToHome(): void {
    this.router.navigate(['/home']);
  }

  async showLoading() {
    this.loadingDialog = await this.loadingController.create({
      message: this.translatePipe.transform('LOADING'),
    });

    this.loadingDialog.present();
  }

  ngOnDestroy() {
    if (this.loadingDialog) {
      this.loadingDialog.dismiss();
    }
    this.backButtonSubscription?.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private handleDataLoadError(): void {
    this.hasError = true;
    this.isLoading = false;
    this.jobs = [];

    if (this.isLoadingPresenting) {
      this.isLoadingPresenting = false;
      this.loadingDialog.dismiss();
    }

    this.snackbarService.open(
      'red',
      'GENERIC_ERROR_MESSAGES.AN_ERROR_OCCURRED_UNABLE_TO_LOAD_DATA'
    );
  }

  private scrollToVisit(visitId: string): void {
    const element = document.getElementById(visitId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      console.warn(`Element with id ${visitId} not found for scrolling`);
    }
  }

  private openTimesheetDialog(type: string) {
    // Use pre-loaded warehouse options for all timesheet activities
    const dialogRef = this.dialog.open(TimesheetDialogComponent, {
      disableClose: true,
      data: {
        actionType: type,
        timesheetTypeOptions: this.timesheetLabels,
        isPaused: this.isPaused,
        warehouseOptions: this.warehouseOptions,
      },
    });
    this.handleTimesheetDialogResult(dialogRef);
  }

  private handleTimesheetDialogResult(dialogRef: any) {
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.timesheetApiService
          .addTimesheet(
            result.timesheetTypeId,
            null,
            result.timesheetCarMileage,
            result.warehouseId
          )
          .subscribe({
            next: () => {
              if (!this.jobs || !Array.isArray(this.jobs)) {
                this.snackbarService.open(
                  'green',
                  'TIMESHEET_EDIT.MESSAGES.TIMESHEET_CREATED'
                );
                return;
              }

              if (result.timesheetTypeId.toString() === '1') {
                this.jobs.forEach((job) => {
                  job.dayStarted = true;
                });
                this.dateStarted = true;
              } else if (result.timesheetTypeId.toString() === '2') {
                this.jobs.forEach((job) => {
                  job.dayEnded = true;
                });
                this.dateEnded = true;
              } else if (result.timesheetTypeId.toString() === '5') {
                this.jobs.forEach((job) => {
                  job.paused = true;
                  if (job.timesheetStartTime && job.technicianStarted) {
                    job.technicianStarted = false;
                    job.timesheetEndTime = true;
                    job.pausedSpecific = true;
                  }
                });
                this.isPaused = true;
              } else if (result.timesheetTypeId.toString() === '999') {
                this.jobs.forEach((job) => {
                  job.paused = false;
                  if (job.pausedSpecific) {
                    job.technicianStarted = true;
                    job.timesheetStartTime = true;
                    job.timesheetEndTime = false;
                    job.pausedSpecific = false;
                  }
                });
                this.isPaused = false;
              } else {
                this.jobs.forEach((job) => {
                  if (job.technicianStarted) {
                    job.technicianStarted = false;
                    job.timesheetEndTime = true;
                    job.timesheetStartTime = true;
                  }
                });
              }
              this.snackbarService.open(
                'green',
                'TIMESHEET_EDIT.MESSAGES.TIMESHEET_CREATED'
              );
            },
            error: () => {
              this.snackbarService.open(
                'red',
                'TIMESHEET_EDIT.MESSAGES.TIMESHEET_CREATE_ERROR'
              );
            },
          });
      }
    });
  }
}
